// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import BulletGrid from '@/components/sections/BulletGrid'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img - reused from the AIR-1000 product page
import DeploymentImage from 'public/img/products/Air1000/air1000_temporary_deployment.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody } from '@/assets/theme/fonts'

export default function ShortTermSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const items = [
    t('shortTerm.items.01'),
    t('shortTerm.items.02'),
    t('shortTerm.items.03'),
    t('shortTerm.items.04'),
    t('shortTerm.items.05'),
    t('shortTerm.items.06')
  ]

  const getContent = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: stacked ? '1fr' : '1fr minmax(0, 28rem)',
        gap: stacked ? '2rem' : '3rem',
        // The photo keeps its own proportions, so it sits at the top.
        alignItems: 'start',
        width: '100%'
      }}
    >
      <Box>
        <SectionHeader
          eyebrow={t('shortTerm.eyebrow')}
          title={t('shortTerm.title')}
          intro={[
            t('shortTerm.intro1'),
            t('shortTerm.intro2'),
            t('shortTerm.intro3')
          ]}
        />

        <BulletGrid items={items} columns={stacked ? 2 : 1} />

        <Typography
          component="p"
          fontSize={getFontBody()}
          sx={{
            marginTop: '2rem',
            paddingLeft: '1rem',
            borderLeft: `3px solid ${themeColors.success.main}`,
            lineHeight: 1.7,
            color: themeColors.gray.defaultText,
            maxWidth: '46rem'
          }}
        >
          {t('shortTerm.outro')}
        </Typography>
      </Box>

      <Box
        sx={{
          lineHeight: 0,
          borderRadius: '10px',
          overflow: 'hidden',
          order: stacked ? -1 : 0
        }}
      >
        <Image
          src={DeploymentImage}
          alt="AIR-1000 device set up for a short term air quality measurement"
          sizes="(max-width: 990px) 100vw, 40vw"
          // Uncropped, and never scaled past its own pixel width.
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: DeploymentImage.width
          }}
        />
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
